import { useTranslation } from 'react-i18next';
import { ShieldCheck, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { PopulatedApproval } from '../../../services/api/policyService';

interface PolicyApprovalCardProps {
    approval: PopulatedApproval;
    onEdit?: () => void;
    onRemove?: () => void;
}

function formatApprovedAt(timestamp: string): string {
    const d = new Date(timestamp);
    const date = d.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
    const time = d.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
    });
    return `${date} · ${time}`;
}

export function PolicyApprovalCard({
    approval,
    onEdit,
    onRemove,
}: PolicyApprovalCardProps) {
    const { t } = useTranslation();
    const showActions = !!onEdit && !!onRemove;
    const editLabel = t('departmentPolicies.table.edit');
    const removeLabel = t('departmentPolicies.foldersDialog.remove');

    return (
        <Card className="overflow-hidden rounded-lg border border-approval-accent bg-approval-surface shadow-none">
            <div className="flex items-center justify-between gap-2 border-b border-approval-accent bg-approval-header px-3.5 py-2.5">
                <div className="flex items-center gap-2">
                    <ShieldCheck
                        className="h-5 w-5 shrink-0 text-white"
                        aria-hidden="true"
                    />
                    <span className="text-sm font-semibold text-emerald-50">
                        {t('departmentPolicies.readPage.approved')}
                    </span>
                </div>
                {showActions && (
                    <div className="flex shrink-0 items-center gap-0.5">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-emerald-50/55 hover:bg-white/10 hover:text-white"
                            onClick={onEdit}
                            aria-label={editLabel}
                        >
                            <Pencil
                                className="h-3.5 w-3.5"
                                aria-hidden="true"
                            />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-emerald-50/55 hover:bg-white/10 hover:text-red-200"
                            onClick={onRemove}
                            aria-label={removeLabel}
                        >
                            <Trash2
                                className="h-3.5 w-3.5"
                                aria-hidden="true"
                            />
                        </Button>
                    </div>
                )}
            </div>

            <div className="space-y-3 px-4 py-3.5">
                <div className="space-y-0.5">
                    <p className="break-words text-sm font-semibold leading-tight text-emerald-50">
                        {approval.approvedBy.name}
                    </p>
                    {approval.approvedBy.role && (
                        <p className="break-words text-xs font-medium leading-snug text-emerald-50/65">
                            {approval.approvedBy.role}
                        </p>
                    )}
                    <p className="break-words text-xs font-medium leading-snug text-emerald-50/50">
                        <span>{formatApprovedAt(approval.approvedAt)}</span>
                    </p>
                </div>

                {approval.comment && (
                    <p className="whitespace-pre-wrap border-t border-emerald-700/25 pt-3 text-sm leading-relaxed text-emerald-50/85">
                        {approval.comment}
                    </p>
                )}
            </div>
        </Card>
    );
}
